import { useRef, useState, type ReactNode } from 'react';
import { ArrowLeftRight, Copy, Import, TerminalSquare, Undo2 } from 'lucide-react';
import { Banner, Button, Card, ConfirmDialog, Fact, Facts, Field, Input } from '@/components/ui';
import { idempotencyKey } from '@/utils/idempotency';
import { useIssueImportToken, useSwitchImportNames } from '@/hooks/useImports';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatBytes, formatDateTime } from '@/utils/format';
import { IMPORT_STATE_LABELS } from '@/utils/labels';
import type { SiteDetail } from '@/types/api';

/**
 * Where an import stands (P7), and the next thing to do: run the commands on
 * the old server, check the new site before anyone is sent there, switch the
 * names — or switch them back.
 */
export function ImportCard({ site, mayManage }: { readonly site: SiteDetail; readonly mayManage: boolean }): ReactNode {
  const [commands, setCommands] = useState<{ command: string; expiresAt: string } | null>(null);
  const [confirming, setConfirming] = useState<null | 'switch' | 'rollback'>(null);
  const [typed, setTyped] = useState('');
  const key = useRef(idempotencyKey('site.import.names'));
  const imp = site.import;

  const issue = useIssueImportToken(site.id, { onSuccess: (result) => setCommands(result) });
  const names = useSwitchImportNames(site.id, {
    onSuccess: (_result, { direction }) => {
      toast.success(direction === 'switch' ? 'Переключение DNS поставлено в очередь' : 'Возврат DNS поставлен в очередь');
      key.current = idempotencyKey('site.import.names');
      setConfirming(null);
      setTyped('');
    },
  });

  if (!imp) return null;
  const canUpload = mayManage && ['awaiting_upload', 'ready', 'failed'].includes(imp.state);
  const address = site.domains[0]?.desiredIpv4;

  return (
    <Card title="Импорт с другого сервера" icon={Import}>
      <Facts>
        <Fact label="Состояние">{IMPORT_STATE_LABELS[imp.state] ?? imp.state}</Fact>
        {imp.uploadedAt ? <Fact label="Выгружен">{formatDateTime(imp.uploadedAt)}</Fact> : null}
        {imp.uploads.length > 0 ? (
          <Fact label="Получено">{imp.uploads.map((upload) => `${upload.name === 'database.sql' ? 'база' : 'файлы'} ${formatBytes(upload.sizeBytes)}`).join(' · ')}</Fact>
        ) : null}
        {imp.meta ? (
          <Fact label="Старый сайт">
            WordPress {imp.meta.wpVersion ?? '?'} · PHP {imp.meta.phpVersion ?? '?'} · префикс таблиц <span className="mono">{imp.meta.tablePrefix}</span>
            {imp.meta.siteUrl ? <span className="muted"> · {imp.meta.siteUrl}</span> : null}
          </Fact>
        ) : null}
        {imp.meta && imp.meta.rootFiles.length > 0 ? <Fact label="Файлы в корне">{imp.meta.rootFiles.join(', ')}</Fact> : null}
        {imp.switchedAt ? <Fact label="Переключён">{formatDateTime(imp.switchedAt)}</Fact> : null}
        {imp.previousAddresses && Object.keys(imp.previousAddresses).length > 0 ? (
          <Fact label="Было до переключения">
            <span className="mono">{Object.entries(imp.previousAddresses).map(([fqdn, ip]) => `${fqdn} → ${ip}`).join(', ')}</span>
          </Fact>
        ) : null}
      </Facts>

      {canUpload ? (
        <div className="stack stack-tight" style={{ marginTop: 'var(--space-4)' }}>
          <p className="field-hint">
            {imp.state === 'awaiting_upload'
              ? 'Выполните команду на старом сервере: она выгрузит базу, wp-content и файлы корня сайта в панель, ничего не изменяя.'
              : 'Перед переключением повторите выгрузку, чтобы перенести последние изменения.'}
          </p>
          {commands ? (
            <>
              <pre className="code-block">{commands.command}</pre>
              <div className="row-tight">
                <Button size="sm" icon={Copy} onClick={() => void navigator.clipboard.writeText(commands.command).then(() => toast.success('Команда скопирована'))}>
                  Скопировать
                </Button>
                <span className="muted text-sm">Действует до {formatDateTime(commands.expiresAt)}; в --path — каталог WordPress</span>
              </div>
            </>
          ) : (
            <div>
              <Button size="sm" icon={TerminalSquare} busy={issue.isPending} onClick={() => issue.mutate()}>
                {imp.state === 'awaiting_upload' ? 'Получить команду для старого сервера' : 'Досинхронизировать'}
              </Button>
            </div>
          )}
          {issue.isError ? <p className="field-error">{errorText(issue.error)}</p> : null}
        </div>
      ) : null}

      {imp.state === 'ready' ? (
        <div className="stack stack-tight" style={{ marginTop: 'var(--space-4)' }}>
          <Banner tone="info" icon={ArrowLeftRight}>
            Проверка до переключения: добавьте в файл hosts строку{' '}
            <span className="mono">
              {address ?? 'IP-сервера'} {site.primaryDomain}
            </span>{' '}
            и откройте сайт в браузере.
          </Banner>
          {mayManage ? (
            <div>
              <Button size="sm" variant="primary" icon={ArrowLeftRight} onClick={() => setConfirming('switch')}>
                Переключить DNS на панель
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {imp.state === 'switched' && mayManage ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Button size="sm" icon={Undo2} onClick={() => setConfirming('rollback')}>
            Вернуть DNS на старый сервер
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirming !== null}
        title={confirming === 'switch' ? 'Переключить DNS на панель?' : 'Вернуть DNS на старый сервер?'}
        description={
          confirming === 'switch'
            ? 'Домены будут направлены на новый сервер; прежние адреса сохраняются для возврата. Домены без разрешённого тега 2d2s переключаются вручную.'
            : 'Домены будут направлены на прежние адреса. Новый сайт сохранится.'
        }
        confirmLabel={confirming === 'switch' ? 'Переключить' : 'Вернуть'}
        destructive={confirming === 'rollback'}
        busy={names.isPending}
        error={names.isError ? errorText(names.error) : undefined}
        onConfirm={() => confirming && names.mutate({ direction: confirming, confirmation: typed.trim(), key: key.current })}
        onClose={() => {
          setConfirming(null);
          setTyped('');
        }}
      >
        <Field label="Для подтверждения введите домен" htmlFor="import-confirm" hint={site.primaryDomain ?? ''}>
          <Input id="import-confirm" value={typed} onChange={(event) => setTyped(event.target.value)} placeholder={site.primaryDomain ?? ''} autoComplete="off" />
        </Field>
      </ConfirmDialog>
    </Card>
  );
}
